import React, { useEffect, useState } from 'react';
import { Radar, ExternalLink } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from '../../lib/adminApi';
import { ADMIN_AUDITS } from '../../constants/testIds';

const GRID_SIZES = ['3', '5', '7'];

const STATUS_STYLES = {
  new: 'bg-amber-100 text-amber-700',
  scanning: 'bg-indigo-100 text-indigo-700',
  failed: 'bg-red-100 text-red-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const LeadRow = ({ lead, onScanStarted }) => {
  const [placeId, setPlaceId] = useState('');
  const [gridSize, setGridSize] = useState('5');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const runScan = async () => {
    if (!placeId) return;
    setError('');
    setLoading(true);
    try {
      await adminApi.post(`/admin/leads/${lead.id}/run-scan`, { place_id: placeId, grid_size: gridSize });
      onScanStarted();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={ADMIN_AUDITS.leadRow} className="bg-white rounded-2xl border border-stone-200 p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-bold text-stone-900">{lead.business_name}</p>
          <p className="text-xs text-stone-500 mt-0.5">
            {lead.email} • wants to rank for "{lead.keyword}" {lead.city ? `in ${lead.city}` : ''}
          </p>
          {lead.error && <p className="text-xs text-red-600 mt-1">{lead.error}</p>}
        </div>
        <span data-testid={ADMIN_AUDITS.leadStatusBadge} className={`text-xs font-semibold rounded-full px-2.5 py-1 shrink-0 ${STATUS_STYLES[lead.status] || ''}`}>
          {lead.status}
        </span>
      </div>

      {(lead.status === 'new' || lead.status === 'failed') && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            data-testid={ADMIN_AUDITS.placeIdInput}
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            placeholder="Paste Google Place ID from your Local Falcon dashboard"
            className="flex-1 px-3.5 py-2.5 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
          />
          <select
            data-testid={ADMIN_AUDITS.gridSizeSelect}
            value={gridSize}
            onChange={(e) => setGridSize(e.target.value)}
            className="px-3 py-2.5 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
          >
            {GRID_SIZES.map((g) => (
              <option key={g} value={g}>{g}x{g} grid</option>
            ))}
          </select>
          <button
            data-testid={ADMIN_AUDITS.runScanButton}
            onClick={runScan}
            disabled={loading || !placeId}
            className="px-5 py-2.5 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] transition disabled:opacity-60 whitespace-nowrap"
          >
            {loading ? 'Starting…' : 'Run scan'}
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {lead.status === 'scanning' && (
        <p className="text-xs text-indigo-600 mt-3">Scanning now — this takes 1-2 minutes. Refresh to check.</p>
      )}
    </div>
  );
};

const AdminAudits = () => {
  const [audits, setAudits] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([adminApi.get('/admin/audits'), adminApi.get('/admin/leads')])
      .then(([auditsRes, leadsRes]) => {
        setAudits(auditsRes.data);
        setLeads(leadsRes.data.filter((l) => l.status !== 'completed'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div data-testid={ADMIN_AUDITS.page}>
      <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Audit history</h1>
      <p className="text-stone-500 text-sm mb-7">Local Falcon heatmap audits from the free homepage tool.</p>

      <h2 className="text-sm font-bold text-stone-800 mb-3 flex items-center gap-2">
        <Radar size={15} className="text-[#F97316]" /> Pending audit requests
      </h2>
      {!loading && leads.length === 0 ? (
        <div data-testid={ADMIN_AUDITS.leadsEmptyState} className="bg-white border border-dashed border-stone-300 rounded-2xl p-8 text-center mb-8">
          <p className="text-stone-500 text-sm">No pending requests. New homepage leads will show up here.</p>
        </div>
      ) : (
        <div className="space-y-3 mb-8">
          {leads.map((lead) => (
            <LeadRow key={lead.id} lead={lead} onScanStarted={load} />
          ))}
        </div>
      )}

      <h2 className="text-sm font-bold text-stone-800 mb-3">Completed audits</h2>
      {!loading && audits.length === 0 ? (
        <div data-testid={ADMIN_AUDITS.emptyState} className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center">
          <p className="text-stone-500 text-sm">
            No audits yet. This will populate once you run a scan on a pending request above.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table data-testid={ADMIN_AUDITS.table} className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-xs uppercase font-semibold">
              <tr>
                <th className="text-left px-5 py-3">Business</th>
                <th className="text-left px-5 py-3">Keyword</th>
                <th className="text-left px-5 py-3">Grid</th>
                <th className="text-left px-5 py-3">SoLV</th>
                <th className="text-left px-5 py-3">Avg rank</th>
                <th className="text-left px-5 py-3">Report</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {audits.map((a) => (
                <tr key={a.id}>
                  <td className="px-5 py-4 font-semibold text-stone-900">{a.business_name}</td>
                  <td className="px-5 py-4 text-stone-600">{a.keyword}</td>
                  <td className="px-5 py-4 text-stone-600">{a.grid_size}</td>
                  <td className="px-5 py-4 text-stone-600">{a.solv ? `${a.solv}%` : '—'}</td>
                  <td className="px-5 py-4 text-stone-600">{a.arp ?? '—'}</td>
                  <td className="px-5 py-4">
                    {a.public_url && (
                      <a href={a.public_url} target="_blank" rel="noreferrer" className="text-[#4F46E5] hover:underline inline-flex items-center gap-1">
                        View <ExternalLink size={12} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminAudits;
